import { useNotificationPermissionState } from "../../notification/hooks/useNotificationPermissionState";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { acquisitionCardDismissedStore } from "../../user/stores/acquisitionCardStore";
import { notificationPromptDismissedAtStore } from "../../user/stores/pushTokensStore";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useStore } from "../../../lib/store";
import type { ChatSuggestion } from "./useChatSuggestion";
import {
  uiMessageRequest,
  type GeneralChatUIMessage,
  type GptChatInstance,
} from "./useGptChat";

/** How long the push ask lets the turn breathe before sliding into the band. */
const PUSH_ASK_DELAY_MS = 2000;

/** The acquisition ask lets a finished answer land before it slides in. */
const ACQUISITION_ASK_DELAY_MS = 2000;

// Whether the reader holds an answer to a question and owes the agent no
// clarification: an open card on the last turn holds the ask behind it.
export function hasFinishedAnswer(
  messages: GeneralChatUIMessage[],
  status: GptChatInstance["status"]
): boolean {
  const last = messages.at(-1);
  if (
    last !== undefined &&
    last.role === "assistant" &&
    uiMessageRequest(last) !== null
  )
    return false;
  const settled = status === "ready" ? messages : messages.slice(0, -1);
  // An opener before the first question answers nothing.
  const firstQuestion = settled.findIndex((message) => message.role === "user");
  if (firstQuestion === -1) return false;
  return settled
    .slice(firstQuestion + 1)
    .some(
      (message) =>
        message.role === "assistant" && uiMessageRequest(message) === null
    );
}

// What the JuisciGPT conversation's composer band shows: the agent's proposal,
// the push ask, the acquisition ask, or neither.
export function useComposerBand(
  suggestion: ChatSuggestion | null,
  questionCount: number,
  answerFinished: boolean
): {
  suggestion: ChatSuggestion | null;
  showPushOptIn: boolean;
  showAcquisitionAsk: boolean;
} {
  const { permissionState } = useNotificationPermissionState();
  const [pushDismissedAt] = useStore(notificationPromptDismissedAtStore);
  const { currentUser } = useCurrentUser();
  const [acquisitionDismissed] = useStore(acquisitionCardDismissedStore);

  // The push ask sits above the composer from the moment the user asks their
  // FIRST question — so it is already on screen while the agent is thinking,
  // which is the dead time it is meant to use, rather than appearing under a
  // finished answer and pushing the transcript as the text lands.
  //
  // Fully derived: answering writes `notificationPromptDismissedAtStore`, and
  // that write is what retires the card. Nothing to do in `onDone`.
  const mayAskForPush =
    questionCount > 0 &&
    // Answered once on this install, "Maybe later" included.
    pushDismissedAt === null &&
    permissionState !== null &&
    // Only "we have never asked" — granted and denied are both decisions.
    permissionState.status === "notDetermined";

  // The ask waits before appearing: landing it in the same frame as the user's
  // question makes it read as a reaction to pressing send, and it would reflow
  // the band while the "Thinking…" row is still settling.
  //
  // Only the APPEARANCE is delayed. `mayAskForPush` gates the debounced value,
  // so answering retires the card in the same frame instead of leaving it up
  // for another two seconds.
  const pushAskSettled = useDebouncedValue(mayAskForPush, PUSH_ASK_DELAY_MS);
  const showPushOptIn = mayAskForPush && pushAskSettled;

  // A proposal TAKES THE BAND OVER (`bandWidget`), push ask included, so the two
  // are arbitrated here, and the answer depends on which one a wait destroys.
  //
  // A ROOM wins. Hiding the push ask costs it nothing: only an ANSWER retires
  // it, so it comes back the moment the card leaves the band. A room withheld
  // costs the room for good: the server spends the conversation's one proposal
  // when it emits it (rule 1 reads the transcript), and the proposal lives only
  // on the last turn, so the next question drops it with no impression. Holding
  // the room behind the ask was how every first question from a user who had
  // never answered it lost its room card.
  //
  // A TOPIC waits. Interest cards come on most clinical turns and nothing
  // rations them, so letting them through would hide the ask under nearly every
  // answer, while one lost to the next question is proposed again later.
  // `permissionState === null` counts as unresolved: it is the state where we do
  // not YET know whether this user has anything to answer.
  const pushAskUnresolved = permissionState === null || mayAskForPush;

  // The phone's signup no longer asks "How did you hear about us?": the first
  // finished answer does (the desktop asks on the home). It ranks after the
  // push ask and before the topics, for the same reasons: an answer or "Later"
  // retires it, and a topic lost to it is proposed again.
  const acquisitionAskPending =
    !isDesktopShell() &&
    currentUser !== null &&
    currentUser.acquisitionChannel === null &&
    !acquisitionDismissed &&
    questionCount > 0 &&
    answerFinished;

  const bandSuggestion = (() => {
    if (suggestion === null) return null;
    switch (suggestion.kind) {
      case "room":
        return suggestion;
      case "topics":
      case "visual_abstract":
        return pushAskUnresolved || acquisitionAskPending ? null : suggestion;
      default:
        throw new Error(suggestion satisfies never);
    }
  })();

  // Only a room is left to cover it once the push ask is settled.
  const mayAskAcquisition =
    acquisitionAskPending && !pushAskUnresolved && bandSuggestion === null;
  // Delayed on the way in only, like the push ask.
  const acquisitionAskSettled = useDebouncedValue(
    mayAskAcquisition,
    ACQUISITION_ASK_DELAY_MS
  );

  return {
    suggestion: bandSuggestion,
    showPushOptIn,
    showAcquisitionAsk: mayAskAcquisition && acquisitionAskSettled,
  };
}
